import {
    BusinessObjectPart,
    BusinessObjectPartContext,
    ModelBase,
    QueryBus,
    Weaver
} from '@skylab-shared/core';
import { AddressModel, DocumentClientModel } from '@skylab-shared/dataModel';
import { propertyChangedFilter } from '../../common/property-changed/property-changed-filter';
import { documentDeliveryAddressFilled } from '../../common/serialziation-utils';
import { CommunityTransactionForSaleQuery } from '../../community-transaction/queries/community-transaction-for-sale.query-base';
import { CompanyParametersQuery } from '../../company/queries/company-parameters.query-base';
import { CountryCollectionQuery } from '../../country/queries/country.query-base';
import { isWstoTransaction } from '../../sale/utils/sale-utils';
import { DocumentWstoConsumerCountryCannotBeEmptyError } from '../violations/document-wsto-consumer-country-cannot-be-empty-error';

interface DocumentModelPart extends ModelBase {
    communityTransactionId: number;
    client: DocumentClientModel;
    deliveryAddress: AddressModel;
    wstoShipmentCountryId: number | null;
    wstoConsumerCountryId: number | null;
    wstoAdditionalInformation: string | null;
}

export class DocumentWstoBoPart extends BusinessObjectPart<DocumentModelPart> {

    public constructor(
        weaver: Weaver,
        protected readonly queryBus: QueryBus
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<DocumentModelPart>): Promise<void> {
        await super.initializeAsync(context);

        const transactions = this.bo.localContext.getPreloaded(CommunityTransactionForSaleQuery)!;

        const { systemCountryId } = this.bo.localContext.getPreloaded(CompanyParametersQuery)!;
        const countries = this.bo.localContext.filterPreloaded(CountryCollectionQuery, { euMember: true })
            .filter(elem => elem.id !== systemCountryId);

        this.coreFeature.propertyChanged.listen<DocumentModelPart>(propertyChangedFilter(this.modelType, ['communityTransactionId'],
            () => {
                const wstoSet = isWstoTransaction(transactions, this.bo.model.communityTransactionId);
                if (wstoSet) {
                    const isDeliveryAddressFilled = documentDeliveryAddressFilled(this.bo.model.deliveryAddress);

                    const countryId = (isDeliveryAddressFilled && this.bo.model.deliveryAddress.countryId) || this.bo.model.client?.address?.countryId;
                    const wstoCountry = countries.find(c => c.id === countryId);

                    this.bo.model.wstoConsumerCountryId = wstoCountry?.id || null;
                    this.bo.model.wstoShipmentCountryId = systemCountryId;
                } else {
                    this.bo.model.wstoShipmentCountryId = this.bo.model.wstoConsumerCountryId = null;
                }
                this.bo.model.wstoAdditionalInformation = null;
            }
        ));

        this.coreFeature.propertyChanged.listen<AddressModel>(propertyChangedFilter(AddressModel, ['countryId'],
            () => {
                if (!documentDeliveryAddressFilled(this.bo.model.deliveryAddress)) return;

                const wstoSet = isWstoTransaction(transactions, this.bo.model.communityTransactionId);
                if (wstoSet) {
                    const countryId = this.bo.model.deliveryAddress.countryId;
                    const isWstoCountry = countries.find(c => c.id === countryId);
                    this.bo.model.wstoConsumerCountryId = isWstoCountry ? countryId : null;
                }
            }
        ));

        this.engine.validation.addRule(
            [[this.modelType, 'communityTransactionId'], [this.modelType, 'wstoConsumerCountryId']],
            [[this.modelType, 'wstoConsumerCountryId']],
            () => {
                const wstoSet = isWstoTransaction(transactions, this.bo.model.communityTransactionId);

                if (!wstoSet) return true;
                return this.bo.model.wstoConsumerCountryId !== null;
            },
            DocumentWstoConsumerCountryCannotBeEmptyError
        );
    }
}
